import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { adminInsert, adminDelete, adminUploadFile } from "@/lib/admin-api";

export interface Material {
  id: number;
  title: string;
  description: string;
  category: string;
  category_label: string;
  file_type: string;
  file_size: string;
  file_url: string;
  download_count: number;
  created_at: string;
  updated_at: string;
}

export function useMaterials() {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMaterials = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from("kangstudy_materials")
        .select("*")
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;
      setMaterials(data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "자료를 불러오지 못했습니다");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  const uploadMaterial = async (
    file: File,
    metadata: { title: string; description: string; category: string; category_label: string }
  ): Promise<Material | null> => {
    const fileExt = file.name.split(".").pop()?.toLowerCase() || "";
    const filePath = `${Date.now()}_${file.name}`;

    // 업로드·등록 모두 관리자 경로다(RLS: 쓰기 service_role 전용).
    // 파일은 Edge Function 이 발급한 서명 URL 로 스토리지에 직접 올린다.
    const fileUrl = await adminUploadFile("kangstudy_materials", filePath, file);
    const fileSize = file.size < 1024 * 1024
      ? `${(file.size / 1024).toFixed(1)}KB`
      : `${(file.size / (1024 * 1024)).toFixed(1)}MB`;

    const fileType = fileExt === "pdf" ? "pdf" : fileExt === "ppt" || fileExt === "pptx" ? "ppt" : fileExt;

    const inserted = await adminInsert("kangstudy_materials", [
      {
        title: metadata.title,
        description: metadata.description,
        category: metadata.category,
        category_label: metadata.category_label,
        file_type: fileType,
        file_size: fileSize,
        file_url: fileUrl,
        download_count: 0,
      },
    ]);

    const row = inserted[0] as unknown as Material | undefined;
    if (!row) throw new Error("자료 등록 실패: 저장된 행을 받지 못했습니다.");

    setMaterials((prev) => [row, ...prev]);
    return row;
  };

  const deleteMaterial = async (id: number) => {
    await adminDelete("kangstudy_materials", [id]);
    setMaterials((prev) => prev.filter((m) => m.id !== id));
  };

  const incrementDownload = async (id: number) => {
    const material = materials.find((m) => m.id === id);
    if (!material) return;

    // 다운로드 카운터는 비로그인 방문자도 올린다 — 테이블 update 를 열지 않고
    // download_count 만 건드리는 RPC 를 쓴다(20260818030000 마이그레이션).
    const { error: rpcError } = await supabase.rpc("increment_material_download", { p_id: id });

    if (!rpcError) {
      setMaterials((prev) =>
        prev.map((m) =>
          m.id === id ? { ...m, download_count: (m.download_count || 0) + 1 } : m
        )
      );
    }
  };

  return { materials, loading, error, fetchMaterials, uploadMaterial, deleteMaterial, incrementDownload };
}